import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, message, theme } from 'antd'
import { BellOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import classNames from 'classnames'

import { locationSelector } from 'app-redux/selectors'
import usePushPermission from 'utils/hooks/usePushPermission'
import { getMobileOperatingSystem } from 'utils/helper'
import './PushNotificationPrompt.scss'

// Beri halaman waktu selesai dimuat dulu, supaya ajakan bukan hal pertama yang
// dilihat user dan tidak menggeser konten yang masih dirender.
const PUSH_PROMPT_DELAY_MS = 4000

// Petakan token antd -> CSS variable, supaya SCSS ikut theme light/dark otomatis.
const buildPushPromptCssVars = (token) => ({
  '--pp-primary': token.colorPrimary,
  '--pp-primary-bg': token.colorPrimaryBg,
  '--pp-primary-border': token.colorPrimaryBorder,
  '--pp-surface': token.colorBgContainer,
  '--pp-border': token.colorBorderSecondary,
  '--pp-text': token.colorText,
  '--pp-muted': token.colorTextSecondary,
  '--pp-radius': `${token.borderRadiusLG}px`,
  '--pp-shadow': token.boxShadowTertiary,
})

/**
 * PushNotificationPrompt - Banner ajakan mengaktifkan notifikasi browser,
 * pengganti slidedown OneSignal. Native prompt browser hanya dipanggil dari
 * klik "Aktifkan" (Safari mewajibkan user gesture; Chrome meredam prompt tanpa
 * interaksi).
 *
 * Tidak dirender di perangkat mobile karena di sana InstallPromptWrapper sudah
 * mengarahkan user ke aplikasi mobile. Setelah "Nanti Saja", banner
 * disembunyikan 7 hari; push tetap bisa diaktifkan dari Pengaturan > Notifikasi.
 */
const PushNotificationPrompt = () => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const { status, pending, isPromptDismissed, enable, dismissPrompt } = usePushPermission()
  const pathname = useSelector(locationSelector)?.pathname || ''
  const [delayPassed, setDelayPassed] = useState(false)

  const isMobileDevice = useMemo(() => getMobileOperatingSystem() !== 'other', [])
  const eligible = status === 'default' && !isPromptDismissed && !isMobileDevice
  const cssVars = useMemo(() => buildPushPromptCssVars(token), [token])

  useEffect(() => {
    if (!eligible) {
      return undefined
    }
    const timer = setTimeout(() => {
      setDelayPassed(true)
    }, PUSH_PROMPT_DELAY_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [eligible])

  const handleEnable = useCallback(async () => {
    try {
      const result = await enable()
      if (result === 'granted') {
        message.success(formatMessage({ id: 'notification.push_enabled_success' }))
      } else if (result === 'denied') {
        message.warning(formatMessage({ id: 'notification.push_denied_hint' }))
      }
    } catch {
      message.error(formatMessage({ id: 'notification.push_enable_failed' }))
    }
  }, [enable, formatMessage])

  if (!eligible || !delayPassed) {
    return null
  }

  return (
    <div
      className={classNames('push-prompt', 'hide-on-print', {
        // Hanya route ini yang menolkan padding .utils__content, lihat
        // `isDashboard` di layouts/Main.
        'push-prompt--inset': pathname === '/dashboard',
      })}
      style={cssVars}
      role="status"
    >
      <span className="push-prompt__illus">
        <BellOutlined />
      </span>
      <div className="push-prompt__body">
        <div className="push-prompt__title">
          {formatMessage({ id: 'notification.push_prompt_title' })}
        </div>
        <div className="push-prompt__desc">
          {formatMessage({ id: 'notification.push_prompt_description' })}
        </div>
      </div>
      <div className="push-prompt__actions">
        <Button
          type="primary"
          className="push-prompt__btn"
          loading={pending}
          onClick={handleEnable}
        >
          {formatMessage({ id: 'notification.push_prompt_enable' })}
        </Button>
        <Button className="push-prompt__btn" disabled={pending} onClick={dismissPrompt}>
          {formatMessage({ id: 'notification.later' })}
        </Button>
      </div>
    </div>
  )
}

export default PushNotificationPrompt
